/**
 * ExprField.tsx — a CEL-compatible expression input with live evaluation.
 *
 * Used by: gate `formula`, edge-connection `expr`, metric `expr`. Renders a
 * text field plus a small live-eval panel: `= <number>` or `Error: <msg>`
 * against the supplied scope, recomputed on every keystroke. The walker is
 * sandboxed (no eval); errors are caught and shown, never thrown to the
 * caller — so a half-typed formula doesn't break the editor.
 *
 * The scope is whatever the caller is evaluating against — for a gate it's
 * the node's runtime scope (self.*, nodes.*, edges.*); for an edge
 * connection it's `{x, params, source, target}`; for a metric it's the
 * graph scope. We don't construct the scope here; the caller passes it.
 */
import { useMemo } from 'react';
import { tryEvalExpr } from '../engine/expr/walker';

interface ExprFieldProps {
  label?: string;
  value: string;
  placeholder?: string;
  scope: Record<string, unknown>;
  onChange: (next: string) => void;
  /** When false, hide the live-eval panel (e.g. for very tight spaces). */
  showEval?: boolean;
  /** Render the expression as a multi-line <textarea> instead of a single-line
   *  <input>. Use for longer formulas (e.g. metric expressions). */
  multiLine?: boolean;
}

export function ExprField({
  label,
  value,
  placeholder,
  scope,
  onChange,
  showEval = true,
  multiLine = false,
}: ExprFieldProps): JSX.Element {
  const result = useMemo(() => tryEvalExpr(value || '0', scope), [value, scope]);
  return (
    <label className="expr-field">
      {label && <div className="param-slider-head"><span>{label}</span></div>}
      {multiLine ? (
        <textarea
          className="text-input expr-input expr-textarea"
          value={value}
          placeholder={placeholder ?? ' CEL expression, e.g. self.stock > 5 ? self.inflow : 0'}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          rows={3}
        />
      ) : (
        <input
          type="text"
          className="text-input expr-input"
          value={value}
          placeholder={placeholder ?? ' CEL expression, e.g. self.stock > 5 ? self.inflow : 0'}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
        />
      )}
      {showEval && (
        <div className={`expr-eval ${result.error ? 'err' : 'ok'}`}>
          {result.error ? `⚠ ${result.error}` : `= ${formatNum(result.value)}`}
        </div>
      )}
    </label>
  );
}

function formatNum(n: number | null): string {
  if (n === null) return '—';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(3);
}
